import React, { useEffect, useState, useCallback, useRef } from "react";
import client, { apiError } from "@/lib/api";
import { CashMovement, CategoriesResponse, Debt } from "@/lib/types";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, GroupBadge, EmptyState } from "@/components/shared";
import CashMovementDetailPanel from "@/components/CashMovementDetailPanel";
import CashBalanceCard from "@/components/CashBalanceCard";
import RegisterMovementDialog from "@/components/RegisterMovementDialog";
import OpeningBalanceDialog from "@/components/OpeningBalanceDialog";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowDownToLine, ArrowUpFromLine, Loader2, Trash2, Search, Flag, ChevronDown, ChevronUp, Download } from "lucide-react";

const exportCsv = (rows: CashMovement[]) => {
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = ["fecha,tipo,monto,descripcion,categoria,canal"].concat(
    rows.map((m) => [String(m.date || "").slice(0, 10), m.type === "inflow" ? "entrada" : "salida", m.amount, m.description, m.category, (m as any).tags?.canal || ""].map(esc).join(","))
  );
  const url = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `caja_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
import { Input } from "@/components/ui/input";
import { DateEditInline } from "@/components/DateEditInline";
import { presetRange, PRESET_LABELS, PresetKey, formatRange } from "@/lib/dates";

const CashPage: React.FC = () => {
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [categories, setCategories] = useState<CategoriesResponse | null>(null);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);
  const [classification, setClassification] = useState("all");
  const [cstatus, setCstatus] = useState("all");
  const [direction, setDirection] = useState("all");
  const [channel, setChannel] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [preset, setPreset] = useState<PresetKey>("todo");
  const [custom, setCustom] = useState<{ date_from: string; date_to: string }>({ date_from: "", date_to: "" });
  const range = preset === "custom" ? { date_from: custom.date_from || null, date_to: custom.date_to || null } : presetRange(preset);
  const [selected, setSelected] = useState<string | null>(null);
  const [balanceKey, setBalanceKey] = useState(0);
  const reqId = useRef(0);
  const [openingDate, setOpeningDate] = useState<string | null>(null);
  const [showOld, setShowOld] = useState(false);

  const loadOpening = useCallback(() => {
    client.get("/cash/opening-balance").then((r) => setOpeningDate(r.data.exists ? (r.data.date || "").slice(0, 10) : null));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    const params: any = {};
    if (classification !== "all") params.classification = classification;
    if (cstatus !== "all") params.classification_status = cstatus;
    if (direction !== "all") params.direction = direction;
    if (channel !== "all") params.channel = channel;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    const r = range;
    if (r.date_from) params.date_from = r.date_from;
    if (r.date_to) params.date_to = r.date_to;
    const res = await client.get("/cash/movements", { params });
    if (id !== reqId.current) return;
    setMovements(res.data);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classification, cstatus, direction, channel, debouncedSearch, preset, custom]);

  useEffect(() => {
    client.get("/cash/categories").then((r) => setCategories(r.data));
    client.get("/debts").then((r) => setDebts(r.data));
    loadOpening();
  }, [loadOpening]);

  useEffect(() => {
    load();
  }, [load]);

  const catLabel = (key: string): string => {
    if (!categories) return key;
    for (const items of Object.values(categories.groups)) {
      const f = items.find((i) => i.key === key);
      if (f) return f.label;
    }
    return key;
  };

  const removeMovement = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este movimiento? Esta acción queda auditada.")) return;
    try {
      await client.delete(`/cash/movements/${id}`);
      toast.success("Movimiento eliminado");
      load();
      setBalanceKey((k) => k + 1);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="cash-page">
      <PageHeader
        title="Caja"
        subtitle={`Movimientos de caja · Periodo: ${formatRange(range)}`}
        actions={
          <div className="flex flex-wrap justify-end gap-2">
            <RegisterMovementDialog fab categories={categories} onCreated={() => { load(); setBalanceKey((k) => k + 1); client.get("/debts").then((r) => setDebts(r.data)); }} />
            <OpeningBalanceDialog onSaved={() => { load(); loadOpening(); setBalanceKey((k) => k + 1); }} />
            <button
              onClick={() => exportCsv(movements)}
              data-testid="cash-export-csv"
              className="flex items-center gap-2 rounded-none border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Download className="h-4 w-4" /> Exportar
            </button>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por palabra…"
                data-testid="cash-search-input"
                className="h-9 w-56 rounded-none border-slate-300 pl-8"
              />
            </div>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-40 rounded-none border-slate-300" data-testid="cash-channel-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los canales</SelectItem>
                <SelectItem value="Shopify">Shopify</SelectItem>
                <SelectItem value="Mercado Libre">Mercado Libre</SelectItem>
                <SelectItem value="Amazon">Amazon</SelectItem>
              </SelectContent>
            </Select>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger className="w-56 rounded-none border-slate-300" data-testid="cash-classification-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las clasificaciones</SelectItem>
                <SelectItem value="unclassified">Sin clasificar</SelectItem>
                {categories &&
                  Object.entries(categories.group_labels)
                    .filter(([g]) => g !== "pendientes")
                    .map(([g, label]) => (
                      <SelectItem key={g} value={g}>
                        {label}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-36 rounded-none border-slate-300" data-testid="cash-direction-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Entradas y salidas</SelectItem>
                <SelectItem value="inflow">Solo entradas</SelectItem>
                <SelectItem value="outflow">Solo salidas</SelectItem>
              </SelectContent>
            </Select>
            <Select value={cstatus} onValueChange={setCstatus}>
              <SelectTrigger className="w-44 rounded-none border-slate-300" data-testid="cash-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {categories?.classification_states.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={preset} onValueChange={(v) => setPreset(v as PresetKey)}>
              <SelectTrigger className="w-40 rounded-none border-slate-300" data-testid="cash-date-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_LABELS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.key === "custom" ? "Fechas personalizadas" : p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {preset === "custom" && (
              <div className="flex items-center gap-1.5" data-testid="cash-custom-range">
                <Input type="date" value={custom.date_from} onChange={(e) => setCustom({ ...custom, date_from: e.target.value })} data-testid="cash-date-from" className="h-9 w-40 rounded-none border-slate-300" />
                <span className="text-xs text-slate-400">a</span>
                <Input type="date" value={custom.date_to} onChange={(e) => setCustom({ ...custom, date_to: e.target.value })} data-testid="cash-date-to" className="h-9 w-40 rounded-none border-slate-300" />
              </div>
            )}
          </div>
        }
      />

      <CashBalanceCard reloadSignal={balanceKey} />

      {(() => {
        const opening = movements.filter((m) => m.category === "saldo_inicial");
        const rest = movements.filter((m) => m.category !== "saldo_inicial");
        const recent = openingDate ? rest.filter((m) => (m.date || "").slice(0, 10) >= openingDate) : rest;
        const old = openingDate ? rest.filter((m) => (m.date || "").slice(0, 10) < openingDate) : [];
        const visible = [...recent, ...opening, ...(showOld ? old : [])];
        return (
          <>
      {!loading && movements.length > 0 && (() => {
        const real = visible.filter((m) => m.category !== "saldo_inicial");
        const inTot = real.filter((m) => m.type === "inflow").reduce((s, m) => s + m.amount, 0);
        const outTot = real.filter((m) => m.type === "outflow").reduce((s, m) => s + m.amount, 0);
        return (
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="cash-filter-totals">
            <div className="border border-slate-200 bg-white p-4">
              <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Entradas (filtro)</span>
              <div className="mt-2 text-xl font-medium tabular text-emerald-600">{money(inTot)}</div>
            </div>
            <div className="border border-slate-200 bg-white p-4">
              <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Salidas (filtro)</span>
              <div className="mt-2 text-xl font-medium tabular text-red-600">{money(outTot)}</div>
            </div>
            <div className="border border-slate-200 bg-white p-4">
              <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Neto (filtro)</span>
              <div className={`mt-2 text-xl font-medium tabular ${inTot - outTot >= 0 ? "text-slate-900" : "text-red-600"}`}>{money(inTot - outTot)}</div>
            </div>
          </div>
        );
      })()}

      <div className="border border-slate-200 bg-white">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
          </div>
        ) : movements.length === 0 ? (
          <EmptyState message="No hay movimientos para los filtros seleccionados" testid="cash-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="cash-table">
            <thead>
              <tr className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3">Clasificación</th>
                <th className="px-5 py-3 text-right">Monto</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((m) => {
                const isOpening = m.category === "saldo_inicial";
                const isDeposit = !!(m as any).is_deposit;
                return (
                <tr
                  key={m.id}
                  onClick={() => { if (!isDeposit) setSelected(m.id); }}
                  className={`border-b border-slate-50 hover:bg-slate-50/50 ${isDeposit ? "" : "cursor-pointer"} ${isOpening ? "bg-slate-50" : ""}`}
                  data-testid={`cash-row-${m.id}`}
                >
                  <td className="px-5 py-3 text-slate-500" onClick={(e) => { if (isDeposit) e.stopPropagation(); }}>
                    {isDeposit ? (
                      <DateEditInline
                        date={m.date}
                        testid={`deposit-date-${m.id}`}
                        onSave={async (d) => {
                          await client.patch(`/platform-deposits/${m.id}/date`, { date: d });
                          load();
                          setBalanceKey((k) => k + 1);
                        }}
                      />
                    ) : (
                      fmtDate(m.date)
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 text-slate-700">
                      {isOpening ? (
                        <Flag className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                      ) : m.type === "inflow" ? (
                        <ArrowDownToLine className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                      ) : (
                        <ArrowUpFromLine className="h-4 w-4 text-red-600" strokeWidth={1.5} />
                      )}
                      {m.description}
                      {isOpening && <span className="text-[11px] text-slate-400">(punto de partida, no es ingreso)</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {m.status === "unclassified" ? (
                      <GroupBadge group="pendientes" label="Sin clasificar" testid={`cash-status-${m.id}`} />
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <GroupBadge group={m.group} label={catLabel(m.category)} testid={`cash-status-${m.id}`} />
                        {m.classification_status === "con_diferencia" && (
                          <span className="text-[10px] font-medium text-red-600">⚠ con diferencia</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td
                    className={`px-5 py-3 text-right font-medium tabular ${
                      isOpening ? "text-slate-700" : m.type === "inflow" ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {isOpening ? "" : m.type === "inflow" ? "+" : "−"}
                    {money(m.amount)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {!m.related_debt_id && !isOpening && !isDeposit && (
                      <button
                        onClick={(e) => removeMovement(m.id, e)}
                        data-testid={`delete-movement-${m.id}`}
                        title="Eliminar movimiento"
                        className="rounded-sm p-1.5 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {old.length > 0 && (
                <tr className="border-t border-slate-200">
                  <td colSpan={5} className="p-0">
                    <button
                      onClick={() => setShowOld((v) => !v)}
                      data-testid="toggle-old-movements"
                      className="flex w-full items-center justify-center gap-2 bg-slate-50 py-3 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#DD0000]"
                    >
                      {showOld ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      {showOld
                        ? `Ocultar ${old.length} movimientos anteriores al saldo inicial`
                        : `Ver ${old.length} movimientos anteriores al saldo inicial (${fmtDate(openingDate || "")})`}
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <CashMovementDetailPanel
        movementId={selected}
        categories={categories}
        debts={debts}
        onClose={() => setSelected(null)}
        onClassified={load}
      />
          </>
        );
      })()}
    </div>
  );
};

export default CashPage;
